import { loadContentData } from "@src/packages/content/loader";
import {
  getDataPointForLoincCode,
  getDataPointForMarkerName,
} from "@src/packages/content/biomarkers";

loadContentData();

/**
 * Normalize a marker label for the fallback identity. Diacritics and display
 * punctuation are presentation details, so they should not split a result's
 * history into separate series.
 */
export function normalizeBiomarkerName(
  markerName: string | null | undefined,
): string {
  return (markerName ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    // Keep letters and numbers from every script. Unknown labels should not
    // collapse to the same empty identity just because they are non-Latin.
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim()
    .replace(/\s+/g, " ");
}

/**
 * Return the stable identity for a biomarker label.
 *
 * Content data points carry the app's canonical aliases (for example AMH,
 * Anti-Mullerian Hormone, and the localized insight label all resolve to the
 * same data point). Unknown markers still get a deterministic normalized-name
 * identity without guessing that two unrelated tests are equivalent.
 */
export function getBiomarkerIdentity(
  markerName: string | null | undefined,
): string {
  const normalized = normalizeBiomarkerName(markerName);
  if (!normalized) return "name:";

  const dataPoint = getDataPointForMarkerName(markerName ?? "");
  if (dataPoint) return `content:${dataPoint.id}`;

  return `name:${normalized}`;
}

/**
 * The identity of a result that carries a clinical code.
 *
 * The code is tried first because it is the measurement's real identity: a
 * canonical observation's label is the coding catalogue's display string,
 * which a patient-facing marker name never matches. Falling back to the
 * label keeps every uncoded and issuer-scoped result behaving as before.
 */
export function getCodedBiomarkerIdentity(result: {
  loincCode?: string | null;
  name?: string | null;
}): string {
  const dataPoint = getDataPointForLoincCode(result.loincCode);
  if (dataPoint) return `content:${dataPoint.id}`;
  return getBiomarkerIdentity(result.name);
}

export function biomarkerNamesMatch(
  left: string | null | undefined,
  right: string | null | undefined,
): boolean {
  return getBiomarkerIdentity(left) === getBiomarkerIdentity(right);
}
